/** Imports */

/** Green Sock - Import */
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

/** Splide Sliders - Import */
import Splide from '@splidejs/splide'

/** VCards (If Needed) - Import */
import vCardsJS from 'vcards-js'

/** XState Machine - Import */
import { createMachine, interpret } from '@xstate/fsm'

/** Quicklinks - Import + Listen Function */
import { listen } from 'quicklink'
listen()

/** YouTube API */
// import YouTubePlayer from 'youtube-player';

/** Base Functions */

/** Green Sock - Base Functions */
gsap.registerPlugin(ScrollTrigger)

/** Splide - Custom Scroll Bar Init */
function sliderProgressBarInIt (sliderElement, slider, barWrapper) {
  const progressBar = barWrapper.querySelector('.splide__progress-bar')
  const lastSlide = sliderElement.querySelector('.splide__slide:last-of-type')
  const slideTrack = sliderElement.querySelector('.splide__track')
  const splideList = sliderElement.querySelector('.splide__list')
  let sliderPlacement
  let slideEnd
  let trackEnd
  let percentPerPixel
  let progressSize
  let delayFix

  // Function to set the width of the Progress Bar
  slider.on('mounted', function (data) {
    const slides = slider.Components.Slides.get(false)
    const slidesLength = slides.length
    const wrapperComputedStyle = window.getComputedStyle(slideTrack, null)
    const slidePaddingRight = parseFloat(wrapperComputedStyle.paddingRight)
    slideEnd = lastSlide.getBoundingClientRect().right
    trackEnd = slideTrack.getBoundingClientRect().right - (slidePaddingRight)
    if (slideEnd < trackEnd) {
      progressSize = 100
    } else {
      progressSize = 100 / slidesLength
    }

    progressBar.style.width = progressSize + '%'
  })

  // Setting the Slide End and Track End variable to get the percentage per pixel
  slider.on('ready', function () {
    const wrapperComputedStyle = window.getComputedStyle(slideTrack, null)
    const slidePaddingRight = parseFloat(wrapperComputedStyle.paddingRight)

    slideEnd = lastSlide.getBoundingClientRect().right
    trackEnd = slideTrack.getBoundingClientRect().right - (slidePaddingRight)
    const diff = slideEnd - trackEnd
    percentPerPixel = diff / 100
  })

  function calculateProgressBarPosition () {
    const lastSliderCardPosition = lastSlide.getBoundingClientRect().right
    let percentage

    if (lastSliderCardPosition >= slideEnd) {
      percentage = 0
    } else if (lastSliderCardPosition <= trackEnd) {
      percentage = 100
    } else {
      const pixelDiff = slideEnd - lastSliderCardPosition
      percentage = pixelDiff / percentPerPixel
    }
    const rightEdge = barWrapper.offsetWidth - progressBar.offsetWidth
    progressBar.style.left = ((rightEdge / 100) * percentage) + 'px'
  }

  // Doing some math on move / drag to move the progress bar along as the splider moves
  slider.on('move dragging', calculateProgressBarPosition)

  // Ensuring the same is done above on arrow click
  slider.on('arrows:mounted', function (prev, next) {
    prev.addEventListener('click', function () {
      delayFix = setInterval(calculateProgressBarPosition, 25)
    })

    next.addEventListener('click', function () {
      delayFix = setInterval(calculateProgressBarPosition, 25)
    })
  })

  // delay fix
  slider.on('moved dragged', function () {
    delayFix = setInterval(calculateProgressBarPosition, 25)

    setTimeout(() => {
      clearTimeout(delayFix)
    }, 500)
  })

  // Functionality to move the Progress Bar and calculate where the splider should be
  progressBar.onmousedown = function (event) {
    event.preventDefault()

    const shiftX = event.clientX - progressBar.getBoundingClientRect().left

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    function onMouseMove (event) {
      let newLeft = event.clientX - shiftX - barWrapper.getBoundingClientRect().left

      // the pointer is out of progressBarWrapper => lock the progressBar within the bounaries
      if (newLeft < 0) {
        newLeft = 0
      }
      const rightEdge = barWrapper.offsetWidth - progressBar.offsetWidth
      if (newLeft > rightEdge) {
        newLeft = rightEdge
      }

      const slideScrollPercentage = (newLeft / rightEdge) * 100
      sliderPlacement = (percentPerPixel * slideScrollPercentage) * -1

      splideList.style.transform = 'translateX(' + sliderPlacement + 'px)'
      progressBar.style.left = newLeft + 'px'
    }

    function onMouseUp () {
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('mousemove', onMouseMove)

      slider.Components.Controller.scroll(sliderPlacement, 0)
    }
  }

  progressBar.ondragstart = function () {
    return false
  }
}

/* Remove preload class to allow transitions */
document.body.classList.remove('preload')

/**
 * Get the value of a cookie
 * Source: https://gist.github.com/wpsmith/6cf23551dd140fb72ae7
 * @param  {String} name  The name of the cookie
 * @return {String}       The cookie value
 */
function getCookie (name) {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
}

/* Trap Focus */
function trapFocus (element, excludingIn, menuToggle, accessibilityAid) {
  const KEYCODE_TAB = 9
  element.addEventListener('keydown', function (e) {
    const isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB)

    if (!isTabPressed) {
      return
    }

    const focusableEls = Array.from(element.querySelectorAll('a[href]:not(' + excludingIn + ' a), button:not(' + excludingIn + ' textarea):not([disabled]), .tabs a:not(' + excludingIn + ' a):not(.tabs a[aria-selected="false"]):not([disabled]), textarea:not(' + excludingIn + ' textarea):not([disabled]), input[type="text"]:not(' + excludingIn + ' input):not([disabled]), input[type="radio"]:not(' + excludingIn + ' input):not([disabled]), input[type="checkbox"]:not(' + excludingIn + ' input):not([disabled]), input[type="search"]:not(' + excludingIn + ' input):not([disabled]), input[type="submit"]:not(' + excludingIn + ' input):not([disabled]), select:not(' + excludingIn + ' select):not([disabled])'))
    if (focusableEls[0].classList.contains('back-button') && !focusableEls[0].classList.contains('active')) {
      focusableEls.shift()
    }

    const firstFocusableEl = focusableEls[0]
    const lastFocusableEl = focusableEls[focusableEls.length - 1]

    if (e.shiftKey) /* shift + tab */ {
      if (document.activeElement === firstFocusableEl) {
        if (accessibilityAid && (!element.classList.contains('accessibility-aid--active'))) {
          return false
        } else if (menuToggle) {
          e.preventDefault()
          menuToggle.focus()
        } else {
          e.preventDefault()
          lastFocusableEl.focus()
        }
      }
    } else /* tab */ {
      if (document.activeElement === lastFocusableEl) {
        e.preventDefault()
        if (menuToggle) {
          menuToggle.focus()
        } else {
          firstFocusableEl.focus()
        }
      }
    }
  })

  if (menuToggle) {
    menuToggle.addEventListener('keydown', function (e) {
      const isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB)

      if (!isTabPressed) {
        return
      }

      const focusableEls = Array.from(element.querySelectorAll('a[href]:not(' + excludingIn + ' a), button:not(' + excludingIn + ' textarea):not([disabled]), .tabs a:not(' + excludingIn + ' a):not(.tabs a[aria-selected="false"]):not([disabled]), textarea:not(' + excludingIn + ' textarea):not([disabled]), input[type="text"]:not(' + excludingIn + ' input):not([disabled]), input[type="radio"]:not(' + excludingIn + ' input):not([disabled]), input[type="checkbox"]:not(' + excludingIn + ' input):not([disabled]), input[type="search"]:not(' + excludingIn + ' input):not([disabled]), input[type="submit"]:not(' + excludingIn + ' input):not([disabled]), select:not(' + excludingIn + ' select):not([disabled])'))
      if (focusableEls[0].classList.contains('back-button') && !focusableEls[0].classList.contains('active')) {
        focusableEls.shift()
      }

      const lastFocusableEl = focusableEls[focusableEls.length - 1]

      if (e.shiftKey) /* shift + tab */ {
        if (element.classList.contains('active')) {
          e.preventDefault()
          lastFocusableEl.focus()
        }
      }
    })
  }
}

/* Custom Fade In Functions */
function fadeIns () {
  const fadeIns = gsap.utils.toArray('.animate--fadeIn')
  const fadeInUps = gsap.utils.toArray('.animate--fadeInUp')
  const fadeInLefts = gsap.utils.toArray('.animate--fadeInLeft')
  const fadeInRights = gsap.utils.toArray('.animate--fadeInRight')
  let animateStart = 'top 90%'

  if (window.innerWidth > 767) {
    animateStart = 'top 70%'
  }

  // function fadeInBuilder
  function buildFadeIn (element) {
    let delayTiming = 0
    const anim = gsap.fromTo(element, { opacity: 0 }, { duration: 0.5, opacity: 1, delay: delayTiming })
    if (element.dataset.delay != null) {
      delayTiming = element.dataset.delay
    }
    ScrollTrigger.create({
      trigger: element,
      start: animateStart,
      animation: anim,
      toggleActions: 'play none none none',
      once: true
    })
  }

  // fade ins
  fadeIns.forEach((fadeIn) => {
    buildFadeIn(fadeIn)
  })

  // fade in ups
  fadeInUps.forEach((fadeInUp) => {
    if (getCookie('reduce-motion') === 'true') {
      buildFadeIn(fadeInUp)
    } else {
      let delayTiming = 0
      if (fadeInUp.dataset.delay != null) {
        delayTiming = fadeInUp.dataset.delay
      }

      const anim = gsap.fromTo(fadeInUp, { opacity: 0, y: 50 }, { duration: 0.5, opacity: 1, y: 0, delay: delayTiming })
      ScrollTrigger.create({
        trigger: fadeInUp,
        start: animateStart,
        animation: anim,
        toggleActions: 'play none none none',
        once: true
      })
    }
  })

  // fade in lefts
  fadeInLefts.forEach((fadeInLeft) => {
    if (getCookie('reduce-motion') === 'true') {
      buildFadeIn(fadeInLeft)
    } else {
      let delayTiming = 0
      if (fadeInLeft.dataset.delay != null) {
        delayTiming = fadeInLeft.dataset.delay
      }

      const anim = gsap.fromTo(fadeInLeft, { opacity: 0, x: -50 }, { duration: 0.5, opacity: 1, x: 0, delay: delayTiming })
      ScrollTrigger.create({
        trigger: fadeInLeft,
        start: animateStart,
        animation: anim,
        toggleActions: 'play none none none',
        once: true
      })
    }
  })

  // fade in rights
  fadeInRights.forEach((fadeInRight) => {
    if (getCookie('reduce-motion') === 'true') {
      buildFadeIn(fadeInRight)
    } else {
      let delayTiming = 0
      if (fadeInRight.dataset.delay != null) {
        delayTiming = fadeInRight.dataset.delay
      }

      const anim = gsap.fromTo(fadeInRight, { opacity: 0, x: 50 }, { duration: 0.5, opacity: 1, x: 0, delay: delayTiming })
      ScrollTrigger.create({
        trigger: fadeInRight,
        start: animateStart,
        animation: anim,
        toggleActions: 'play none none none',
        once: true
      })
    }
  })
}

/** Block / Layout Functions */

/* Accessibility Functions */
function accessibilityInit () {
  const togglePanelButton = document.getElementById('accessibilityPanelToggle')
  const accessibilityAid = document.getElementById('accessibilityAid')
  const accessibilityPanel = document.getElementById('accessibilityPanel')
  const fontSizeButton = document.getElementById('fontSize')
  const highContrastButton = document.getElementById('highContrast')
  const reduceMotionButton = document.getElementById('reduceMotion')
  const html = document.querySelector('html')
  const videos = document.querySelectorAll('video[autoplay]')
  trapFocus(accessibilityAid, null, false, true)

  function togglePanel () {
    if (accessibilityAid.classList.contains('accessibility-aid--active')) {
      accessibilityAid.classList.remove('accessibility-aid--active')
      togglePanelButton.setAttribute('aria-expanded', 'false')
      accessibilityPanel.setAttribute('aria-hidden', 'true')
      accessibilityPanel.style.display = 'none'
      togglePanelButton.focus()
    } else {
      accessibilityPanel.style.display = 'flex'
      togglePanelButton.setAttribute('aria-expanded', 'true')
      accessibilityPanel.setAttribute('aria-hidden', 'false')

      setTimeout(() => {
        accessibilityAid.classList.add('accessibility-aid--active')
        accessibilityPanel.focus()
      }, 25)
    }
  }

  togglePanelButton.addEventListener('click', togglePanel)

  // individual button actions
  if (fontSizeButton) {
    if (getCookie('font-size') === 'bigger') {
      fontSizeButton.classList.add('accessibility-option--active')
      document.documentElement.style.setProperty('font-size', '120%')
    }

    fontSizeButton.addEventListener('click', function () {
      const fontSizeCookie = getCookie('font-size')
      if (fontSizeCookie !== 'bigger') {
        document.cookie = `font-size=bigger;max-age=${60 * 60 * 24 * 7};path=/`
        fontSizeButton.classList.add('accessibility-option--active')
        document.documentElement.style.setProperty('font-size', '120%')
      } else {
        document.cookie = `font-size=initial;max-age=${60 * 60 * 24 * 7};path=/`
        fontSizeButton.classList.remove('accessibility-option--active')
        document.documentElement.style.setProperty('font-size', '100%')
      }
    })
  }
  if (highContrastButton) {
    if (getCookie('high-contrast') === 'true') {
      highContrastButton.classList.add('accessibility-option--active')
    }

    highContrastButton.addEventListener('click', function () {
      const highContrastCookie = getCookie('high-contrast')
      if (highContrastCookie !== 'true') {
        document.cookie = `high-contrast=true;max-age=${60 * 60 * 24 * 7};path=/`
        highContrastButton.classList.add('accessibility-option--active')
        html.classList.add('high-contrast')
      } else {
        document.cookie = `high-contrast=initial;max-age=${60 * 60 * 24 * 7};path=/`
        highContrastButton.classList.remove('accessibility-option--active')
        html.classList.remove('high-contrast')
      }
    })
  }
  if (reduceMotionButton) {
    if (getCookie('reduce-motion') === 'true') {
      reduceMotionButton.classList.add('accessibility-option--active')
    }

    reduceMotionButton.addEventListener('click', function () {
      const reduceMotionCookie = getCookie('reduce-motion')
      if (reduceMotionCookie !== 'true') {
        document.cookie = `reduce-motion=true;max-age=${60 * 60 * 24 * 7};path=/`
        reduceMotionButton.classList.add('accessibility-option--active')
        html.classList.add('reduce-motion')
        gsap.globalTimeline.pause()
        videos.forEach((video) =>
          video.pause()
        )
      } else {
        document.cookie = `reduce-motion=initial;max-age=${60 * 60 * 24 * 7};path=/`
        reduceMotionButton.classList.remove('accessibility-option--active')
        html.classList.remove('reduce-motion')
        gsap.globalTimeline.play()
        videos.forEach((video) =>
          video.play()
        )
      }
    })
  }
}

/* Header Functions */
function headerInIt() {
  // Variables
  const header = document.getElementById("header");
  const headerToggle = document.getElementById("navToggle");
  const nav = document.getElementById("headerNav");
  const mainSubNavButtons = nav.querySelectorAll(".subnav-link");
  const topNav = document.getElementById("headerTop");
  const subNavCloseButtons = nav.querySelectorAll(".header__close-sub");
  let closing;
  const topSubNavTimeouts = [];
  const body = document.querySelector("body");
  const headerScreen = document.querySelector("#headerScreen");
  const hamburgerTop = document.getElementById("hamburgerTop");
  const hamburgerMiddle = document.getElementById("hamburgerMiddle");
  const hamburgerBottom = document.getElementById("hamburgerBottom");
  const hamburgerTl = gsap.timeline({ paused: true });
  const mobileLinks = nav.querySelectorAll(".header__nav-link-mobile");
  //console.log(mobileLinks)

  // Close Other SubNav Function
  function closeOtherSubNav() {
    const activeSubNavs = document.querySelectorAll(".header__main-sub.active");

    activeSubNavs.forEach((element) => {
      const buttonId = element.getAttribute("data-controlledby");
      const button = document.getElementById(buttonId);

      button.setAttribute("aria-expanded", "false");
      button.setAttribute("aria-hidden", "true");
      button.classList.remove("subnav-link--sub-nav-open");
      element.classList.remove("active");
      element.style.display = "none";
    });
  }

  /* Hamburger Animation */
  if (getCookie("reduce-motion") !== "true") {
    hamburgerTl
      .add("start")
      .to(
        hamburgerTop,
        {
          rotation: 45,
          x: "-5px",
          y: "16px",
          transformOrigin: "right 50%",
          duration: ".125",
          ease: "power2.out",
        },
        "start"
      )
      .to(
        hamburgerBottom,
        {
          rotation: "-45",
          x: "-5px",
          y: "-16px",
          transformOrigin: "right 50%",
          duration: ".125",
          ease: "power2.out",
        },
        "start"
      );
  } else {
    hamburgerTl
      .add("start")
      .to(
        hamburgerTop,
        {
          rotation: 45,
          x: "-5px",
          y: "16px",
          transformOrigin: "right 50%",
          duration: ".125",
          ease: "power2.out",
        },
        "start"
      )
      .to(
        hamburgerBottom,
        {
          rotation: "-45",
          x: "-5px",
          y: "-16px",
          transformOrigin: "right 50%",
          duration: ".125",
          ease: "power2.out",
        },
        "start"
      );
  }

  /* Main Nav Header Machine */
  const headerMachine = createMachine({
    id: "header",
    initial: "inactive",
    states: {
      inactive: {
        on: {
          TOGGLE: {
            target: "active",
            actions: () => {
              clearTimeout(closing);

              if (header.classList.contains("closing")) {
                header.classList.remove("closing");
              }

              nav.style.display = "flex";
              header.classList.add("active-nav");
              nav.setAttribute("aria-hidden", "false");
              headerToggle.setAttribute("aria-expanded", "true");
              body.classList.add("nav-active");
              hamburgerTl.play();

              // headerToggle.focus();
              trapFocus(nav, null, headerToggle);
            },
          },
        },
      },
      active: {
        on: {
          TOGGLE: {
            target: "inactive",
            actions: () => {
              header.classList.remove("active-nav");
              header.classList.add("closing");
              header.classList.remove("active-subnav");
              nav.setAttribute("aria-hidden", "true");
              headerToggle.setAttribute("aria-expanded", "false");
              body.classList.remove("nav-active");
              const activeSubNavs = document.querySelectorAll(
                ".header__main-sub.active"
              );
              hamburgerTl.reverse();

              activeSubNavs.forEach((element) => {
                const subNavButtonId =
                  element.getAttribute("data-controlledby");
                const subNavButton = document.getElementById(subNavButtonId);
                const subNavClose = element.querySelector(".header__close-sub");
                subNavButton.setAttribute("aria-expanded", "false");
                subNavClose.setAttribute("aria-expanded", "false");
                element.setAttribute("aria-hidden", "true");
                element.classList.remove("active");
                nav.classList.remove("header__nav--sub-nav-open");
                setTimeout(() => {
                  element.style.display = "none";
                  element.classList.remove("active");
                }, 300);
              });

              closing = setTimeout(() => {
                nav.style.display = "none";
                header.classList.remove("closing");
              }, 300);

              // headerToggle.focus();
            },
          },
        },
      },
    },
  });

  const headerService = interpret(headerMachine).start();

  // Main Nav Functionality
  headerToggle.addEventListener("click", () => {
    headerService.send("TOGGLE");
  });

  // Close menu when links are clicked on mobile
  mobileLinks.forEach((element) => {
    element.addEventListener("click", function () {
      headerService.send("TOGGLE");
    });
  });

  headerScreen.addEventListener("click", function (e) {
    if (header.classList.contains("active-nav")) {
      e.preventDefault();
      headerService.send("TOGGLE");
    }
  });

  window.addEventListener("resize", function (e) {
    if (header.classList.contains("active-nav") && window.innerWidth > 767) {
      e.preventDefault();
      headerService.send("TOGGLE");
    }
  });

  // Main Sub Nav Functionality
  mainSubNavButtons.forEach((element) => {
    element.addEventListener("click", function () {
      const subNavId = element.getAttribute("aria-controls");
      const subNav = document.getElementById(subNavId);
      const subNavClose = subNav.querySelector(".header__close-sub");
      const activeSubNavs = document.querySelectorAll(
        ".header__main-sub.active"
      );
      if (!subNav.classList.contains("active")) {
        if (activeSubNavs.length > 0) {
          closeOtherSubNav();
          subNav.classList.add("active");
          subNav.style.display = "flex";
          header.classList.add("active-subnav");
          element.classList.add("subnav-link--sub-nav-open");
          element.setAttribute("aria-expanded", "true");
          subNavClose.setAttribute("aria-expanded", "true");
          subNav.setAttribute("aria-hidden", "false");
          subNavClose.focus();
          trapFocus(subNav);
        } else {
          subNav.style.display = "flex";
          header.classList.add("active-subnav");
          element.classList.add("subnav-link--sub-nav-open");
          element.setAttribute("aria-expanded", "true");
          subNavClose.setAttribute("aria-expanded", "true");
          subNav.setAttribute("aria-hidden", "false");
          subNavClose.focus();
          trapFocus(subNav);
          setTimeout(() => {
            subNav.classList.add("active");
          }, 10);
        }
      }
    });
  });

  subNavCloseButtons.forEach((element) => {
    element.addEventListener("click", function () {
      const subNavId = element.getAttribute("aria-controls");
      const subNav = document.getElementById(subNavId);
      const subNavButton = document.getElementById(subNavId + "Button");
      const subNavClose = subNav.querySelector(".header__close-sub");
      if (subNav.classList.contains("active")) {
        element.setAttribute("aria-expanded", "false");
        header.classList.remove("active-subnav");
        subNavClose.setAttribute("aria-expanded", "false");
        subNav.setAttribute("aria-hidden", "true");
        subNav.classList.remove("active");
        subNavButton.classList.remove("subnav-link--sub-nav-open");
        subNavButton.focus();
        setTimeout(() => {
          subNav.style.display = "none";
        }, 300);
      }
    });
  });

  // Top Nav Functionality
  if (topNav) {
    const topSubNavButtons = topNav.querySelectorAll(".subnav-link");

    topSubNavButtons.forEach((element, i) => {
      element.addEventListener("click", function () {
        const subNavId = element.getAttribute("aria-controls");
        const subNav = document.getElementById(subNavId);

        if (topSubNavTimeouts[i] != null) {
          clearTimeout(topSubNavTimeouts[i]);
          topSubNavTimeouts[i] = null;
        }

        if (!subNav.classList.contains("active")) {
          subNav.style.display = "block";
          element.setAttribute("aria-expanded", "true");
          subNav.setAttribute("aria-hidden", "false");
          setTimeout(() => {
            subNav.classList.add("active");
          }, 10);
        } else {
          element.setAttribute("aria-expanded", "false");
          subNav.setAttribute("aria-hidden", "true");
          subNav.classList.remove("active");
          subNav.style.display = "none";
        }
      });

      element.parentElement.addEventListener("mouseenter", function () {
        const subNavId = element.getAttribute("aria-controls");
        const subNav = document.getElementById(subNavId);

        document
          .querySelectorAll(".header__top-main-sub.active")
          .forEach((element) => {
            element.classList.remove("active");
            element.style.display = "none";
          });

        if (topSubNavTimeouts[i] != null) {
          clearTimeout(topSubNavTimeouts[i]);
          topSubNavTimeouts[i] = null;
        }

        if (!subNav.classList.contains("active")) {
          subNav.style.display = "block";
          element.setAttribute("aria-expanded", "true");
          subNav.setAttribute("aria-hidden", "false");
          setTimeout(() => {
            subNav.classList.add("active");
          }, 10);
        }
      });

      element.parentElement.addEventListener("mouseleave", function () {
        const subNavId = element.getAttribute("aria-controls");
        const subNav = document.getElementById(subNavId);

        if (topSubNavTimeouts[i] != null) {
          clearTimeout(topSubNavTimeouts[i]);
          topSubNavTimeouts[i] = null;
        }

        if (subNav.classList.contains("active")) {
          topSubNavTimeouts[i] = setTimeout(() => {
            element.setAttribute("aria-expanded", "false");
            subNav.setAttribute("aria-hidden", "true");
            subNav.classList.remove("active");
            subNav.style.display = "none";
          }, 300);
        }
      });
    });
  }

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") {
      if (header.classList.contains("active-nav")) {
        headerService.send("TOGGLE");
      }
    }
  });

  // Header Scrolling
  const initScrollPos =
    window.scrollY ||
    window.scrollTop ||
    document.getElementsByTagName("html")[0].scrollTop;

  if (initScrollPos > 9) {
    header.classList.add("header--scrolling");
  }

  window.addEventListener("scroll", function () {
    const scrollPos =
      window.scrollY ||
      window.scrollTop ||
      document.getElementsByTagName("html")[0].scrollTop;

    if (scrollPos > 9 && !header.classList.contains("header--scrolling")) {
      header.classList.add("header--scrolling");
    } else if (
      scrollPos < 10 &&
      header.classList.contains("header--scrolling")
    ) {
      header.classList.remove("header--scrolling");
    }
  });
}

/* Content Card Slider Functions */
function contentCardSliderInIt () {
  // Variables
  const wrappers = document.querySelectorAll('.content-card-slider')

  wrappers.forEach(element => {
    const sliderWrapper = element.querySelector('.content-card-slider__wrapper.splide')

    // Initializing Spliders
    const progressBarWrapper = sliderWrapper.querySelector('.splide__progress-bar-wrapper')
    const slider = new Splide(sliderWrapper, {
      perPage: 1,
      arrows: true,
      gap: '1rem',
      padding: '1.5rem',
      drag: 'free',
      pagination: false,
      mediaQuery: 'min',
      breakpoints: {
        992: {
          arrows: true,
          padding: '6rem',
          perPage: 3
        },
        767: {
          arrows: true,
          perPage: 2
        },
        481: {
          arrows: true,
          gap: '2rem',
          padding: '2.5rem',
          perPage: 1
        }
      }
    })

    if (progressBarWrapper) {
      sliderProgressBarInIt(sliderWrapper, slider, progressBarWrapper)
    }

    slider.mount()
  })
}

/* Content Timeline Functions */
function contentTimelineInIt () {
  // Variables
  const wrappers = document.querySelectorAll('.content-timeline')

  wrappers.forEach(element => {
    const sliderWrapper = element.querySelector('.content-timeline__wrapper.splide')

    // Initializing Spliders
    const progressBarWrapper = sliderWrapper.querySelector('.splide__progress-bar-wrapper')
    const slider = new Splide(sliderWrapper, {
      perPage: 1,
      arrows: false,
      gap: '1rem',
      padding: '1.5rem',
      drag: 'free',
      pagination: false,
      mediaQuery: 'min',
      breakpoints: {
        992: {
          arrows: false,
          padding: '6rem',
          perPage: 2
        },
        767: {
          arrows: false,
          perPage: 2
        },
        481: {
          arrows: false,
          gap: '0rem',
          padding: '2.5rem',
          perPage: 1
        }
      }
    })

    if (progressBarWrapper) {
      sliderProgressBarInIt(sliderWrapper, slider, progressBarWrapper)
    }

    slider.mount()
  })
}

/* Content Video Slider Functions */
function contentVideoSliderInIt () {
  let wrapper = document.querySelector('.content-video-slider')
  let slider = wrapper.querySelector('.splide')
  let slides = slider.querySelectorAll('.splide__slide')
  let slideWrapper = slider.querySelector('.content-video-slider__image-wrapper')
  let videos = []
  window._wq = window._wq || []

  // event listener for end of ALL videos
  _wq.push({ id: "_all", onReady: function(video) {
    video.bind("end", function(t) {
      video.pause()
      video.time(0)

      const activeWrapper = video.container.closest(".active")
      if(activeWrapper) {
        activeWrapper.classList.remove("active")
      }
    })
  }})

  function stopActiveVideo() {
    let activeVideoWrapper = slider.querySelector('.content-video-slider__image-wrapper.active')
    
    if(activeVideoWrapper) {
      const iframe = activeVideoWrapper.querySelector(".iframe")
      const buttonId = iframe.getAttribute("aria-controlledby")
      const playButton = document.getElementById(buttonId)
      const wistiaEmbed = activeVideoWrapper.querySelector('.wistia_embed')

      if(wistiaEmbed) {
        const wistiaId = wistiaEmbed.id
        activeVideoWrapper.classList.remove("active")
        activeVideoWrapper.setAttribute("aria-hidden", "true")
        playButton.setAttribute("aria-expanded", "false")

        const wistiaVideo = Wistia.api(wistiaId)
        wistiaVideo.pause()
        wistiaVideo.time(0)
      }
    }
  }

  // Initializing Splider
  let videoSlider = new Splide(slider, {
    type: 'slide',
    perPage: 3,
    perMove: 3,
    focus: 'center',
    rewind: true,
    arrows: true,
    gap: '4rem',
    pagination: true,
    padding: { 
      left: '10%', 
      right: '10%' 
    },
    breakpoints: {
      992: {
        gap: '2rem'
      },
      768: {
        gap: '1.5rem',
        perPage: 1,
        perMove: 1,
        padding: { 
          left: '25%', 
          right: '25%' 
        },
      }
    }
  })
  
  videoSlider.mount()

  for(i = 0; i < slides.length; i++) {
    const slide = slides[i]
    const playButton = slide.querySelector('.content-video-slider__button-wrapper')

    playButton.addEventListener('click', function() {
      stopActiveVideo()

      const wistiaEmbed = slide.querySelector('.wistia_embed')

      if(wistiaEmbed) {
        const wistiaId = wistiaEmbed.id
        const wrapper = slide.querySelector('.content-video-slider__image-wrapper')
        wrapper.classList.add("active")
        wrapper.setAttribute("aria-hidden", "false")
        playButton.setAttribute("aria-expanded", "true")

        const wistiaVideo = Wistia.api(wistiaId)
        wistiaVideo.play()
      }
    })
  }
}

/* Content Features Slider Functions */
function contentFeaturesInIt () {
  // Variables
  const sliders = document.getElementsByClassName('content-features__wrapper splide')

  // Initializing Splider
  for (let i = 0; i < sliders.length; i++) {
    new Splide(sliders[i], {
      type: 'slide',
      perPage: 1,
      perMove: 1,
      rewind: true,
      arrows: false,
      gap: '0rem',
      pagination: true,
      padding: { 
        left: '1.25rem', 
        right: '1.25rem' 
      },
      destroy: true,
      breakpoints: {
        992: {
          gap: '0rem',
          perPage: 2,
          destroy: false
        },
        767: {
          gap: '0rem',
          perPage: 1,
        }
      }
    }).mount()
  }
}

/* Related Testimonials Functions */
function relatedTestimonialsInIt () {
  // Variabls
  const wrappers = document.getElementsByClassName('related-testimonials')

  for (let i = 0; i < wrappers.length; i++) {
    const wrapper = wrappers[i]
    const mainSlider = wrapper.querySelector('.related-testimonials__wrapper.splide')
    const navSlider = wrapper.querySelector('.related-testimonials__nav-wrapper.splide')

    const mainSplide = new Splide(mainSlider, {
      type: 'fade',
      perMove: 1,
      rewind: true,
      drag: true,
      snap: true,
      arrows: false,
      gap: '1.25rem',
      pagination: false,
      breakpoints: {
        767: {
          gap: '0rem',
          perPage: 1,
          pagination: true,
          arrows: true
        }
      }
    })

    const navSplide = new Splide(navSlider, {
      type: 'loop',
      perMove: 1,
      perPage: 4,
      rewind: true,
      drag: true,
      snap: true,
      arrows: false,
      gap: '1.25rem',
      pagination: true,
      isNavigation: true,
      breakpoints: {
        768: {
          pagination: true,
        }
      }
    })

    mainSplide.sync( navSplide )
    mainSplide.mount()
    navSplide.mount()
  }
}

/* Logo Grid Functions */
function logoGridInIt () {
  // Variables
  const sliders = document.getElementsByClassName('content-logo-grid__logos-grid splide')

  // Initializing Splider
  for (let i = 0; i < sliders.length; i++) {
    new Splide(sliders[i], {
      type: 'loop',
      perPage: 4,
      perMove: 1,
      rewind: true,
      arrows: false,
      gap: '3rem',
      breakpoints: {
        992: {
          gap: '2rem'
        },
        768: {
          perPage: 2,
          gap: '1rem'
        }
      }
    }).mount()
  }
}

/* Team Card Functions */
function teamCardInit () {
  // variables
  const teamCards = document.querySelectorAll('.card-team')

  teamCards.forEach((team, i) => {
    window['vCard' + i] = vCardsJS()
    const vCardButton = team.querySelector('.vcard-button')
    const phoneNumberButton = team.querySelector('.card-team__phone-number-button')

    // set up the V Card
    if (vCardButton.dataset.name !== undefined && vCardButton.dataset.name !== '') {
      const name = vCardButton.dataset.name.split(' ')
      const nameTotal = name.length

      if (nameTotal > 1) {
        window['vCard' + i].firstName = name[0]
        window['vCard' + i].lastName = name[nameTotal - 1]
        if (nameTotal >= 3) {
          let middleNameStr = ''
          for (let index = 1; index < nameTotal; index++) {
            middleNameStr += name[index]

            if (index !== (nameTotal - 1)) {
              middleNameStr += ' '
            }
          }
          window['vCard' + i].middleName = middleNameStr
        }
      } else {
        window['vCard' + i].firstName = name[0]
      }
    }

    window['vCard' + i].organization = 'COMPANY NAME'
    if (vCardButton.dataset.img !== undefined && vCardButton.dataset.img !== '') {
      window['vCard' + i].photo.attachFromUrl(vCardButton.dataset.img, 'JPEG')
    }
    if (vCardButton.dataset.phone !== undefined && vCardButton.dataset.phone !== '') {
      window['vCard' + i].workPhone = vCardButton.dataset.phone
    }
    if (vCardButton.dataset.email !== undefined && vCardButton.dataset.email !== '') {
      window['vCard' + i].workEmail = vCardButton.dataset.email
    }
    if (vCardButton.dataset.title !== undefined && vCardButton.dataset.title !== '') {
      window['vCard' + i].title = vCardButton.dataset.title
    }

    function saveAs (uri, filename) {
      const link = document.createElement('a')
      if (typeof link.download === 'string') {
        link.href = uri
        link.download = filename

        // Firefox requires the link to be in the body
        document.body.appendChild(link)

        // simulate click
        link.click()

        // remove the link when done
        document.body.removeChild(link)
      } else {
        window.open(uri)
      }
    }

    vCardButton.addEventListener('click', function (e) {
      e.preventDefault()
      saveAs('data:text/x-vcard;urlencoded,' + encodeURIComponent(window['vCard' + i].getFormattedString()), `${String(window['vCard' + i].firstName).toLowerCase()}-${String(window['vCard' + i].lastName).toLowerCase()}.vcf`)
    })

    if (phoneNumberButton) {
      let closingPhone = false

      phoneNumberButton.addEventListener('click', function () {
        const phoneNumberWrapperId = phoneNumberButton.getAttribute('aria-controls')
        const phoneNumberWrapper = document.getElementById(phoneNumberWrapperId)

        if (!phoneNumberWrapper.classList.contains('card-team__phone-number--active')) {
          if (closingPhone !== false) {
            clearTimeout(closingPhone)
          }

          // close active number wrapper
          if (document.querySelector('.card-team__phone-number--active')) {
            const activeNumber = document.querySelector('.card-team__phone-number--active')
            const activeNumberButtonId = activeNumber.getAttribute('aria-controlledBy')
            const activeNumberButton = document.getElementById(activeNumberButtonId)

            activeNumber.classList.remove('card-team__phone-number--active')
            activeNumber.setAttribute('aria-hidden', 'true')
            activeNumberButton.setAttribute('aria-expanded', 'false')

            closingPhone = setTimeout(() => {
              activeNumber.style.display = 'none'
            }, 100)
          }

          // open this wrapper
          phoneNumberWrapper.style.display = 'block'
          phoneNumberWrapper.setAttribute('aria-hidden', 'false')
          phoneNumberButton.setAttribute('aria-expanded', 'true')

          setTimeout(() => {
            phoneNumberWrapper.classList.add('card-team__phone-number--active')
          }, 100)
        } else {
          phoneNumberWrapper.classList.remove('card-team__phone-number--active')
          phoneNumberWrapper.setAttribute('aria-hidden', 'true')
          phoneNumberButton.setAttribute('aria-expanded', 'false')

          closingPhone = setTimeout(() => {
            phoneNumberWrapper.style.display = 'none'
          }, 100)
        }
      })
    }
  })
}

/* Single Team Functions */
function teamSingleInit () {
  // variables
  const teamSingle = document.querySelectorAll('.content-team-single')

  teamSingle.forEach((team, i) => {
    window['vCard' + i] = vCardsJS()
    const vCardButton = team.querySelector('.vcard-button')
    const phoneNumberButton = team.querySelector('.card-team__phone-number-button')

    // set up the V Card
    if (vCardButton.dataset.name !== undefined && vCardButton.dataset.name !== '') {
      const name = vCardButton.dataset.name.split(' ')
      const nameTotal = name.length

      if (nameTotal > 1) {
        window['vCard' + i].firstName = name[0]
        window['vCard' + i].lastName = name[nameTotal - 1]
        if (nameTotal >= 3) {
          let middleNameStr = ''
          for (let index = 1; index < nameTotal; index++) {
            middleNameStr += name[index]

            if (index !== (nameTotal - 1)) {
              middleNameStr += ' '
            }
          }
          window['vCard' + i].middleName = middleNameStr
        }
      } else {
        window['vCard' + i].firstName = name[0]
      }
    }

    window['vCard' + i].organization = 'COMPANY NAME'
    if (vCardButton.dataset.img !== undefined && vCardButton.dataset.img !== '') {
      window['vCard' + i].photo.attachFromUrl(vCardButton.dataset.img, 'JPEG')
    }
    if (vCardButton.dataset.phone !== undefined && vCardButton.dataset.phone !== '') {
      window['vCard' + i].workPhone = vCardButton.dataset.phone
    }
    if (vCardButton.dataset.email !== undefined && vCardButton.dataset.email !== '') {
      window['vCard' + i].workEmail = vCardButton.dataset.email
    }
    if (vCardButton.dataset.title !== undefined && vCardButton.dataset.title !== '') {
      window['vCard' + i].title = vCardButton.dataset.title
    }

    function saveAs (uri, filename) {
      const link = document.createElement('a')
      if (typeof link.download === 'string') {
        link.href = uri
        link.download = filename

        // Firefox requires the link to be in the body
        document.body.appendChild(link)

        // simulate click
        link.click()

        // remove the link when done
        document.body.removeChild(link)
      } else {
        window.open(uri)
      }
    }

    vCardButton.addEventListener('click', function (e) {
      e.preventDefault()
      saveAs('data:text/x-vcard;urlencoded,' + encodeURIComponent(window['vCard' + i].getFormattedString()), `${String(window['vCard' + i].firstName).toLowerCase()}-${String(window['vCard' + i].lastName).toLowerCase()}.vcf`)
    })

    if (phoneNumberButton) {
      let closingPhone = false

      phoneNumberButton.addEventListener('click', function () {
        const phoneNumberWrapperId = phoneNumberButton.getAttribute('aria-controls')
        const phoneNumberWrapper = document.getElementById(phoneNumberWrapperId)

        if (!phoneNumberWrapper.classList.contains('card-team__phone-number--active')) {
          if (closingPhone !== false) {
            clearTimeout(closingPhone)
          }

          // close active number wrapper
          if (document.querySelector('.card-team__phone-number--active')) {
            const activeNumber = document.querySelector('.card-team__phone-number--active')
            const activeNumberButtonId = activeNumber.getAttribute('aria-controlledBy')
            const activeNumberButton = document.getElementById(activeNumberButtonId)

            activeNumber.classList.remove('card-team__phone-number--active')
            activeNumber.setAttribute('aria-hidden', 'true')
            activeNumberButton.setAttribute('aria-expanded', 'false')

            closingPhone = setTimeout(() => {
              activeNumber.style.display = 'none'
            }, 100)
          }

          // open this wrapper
          phoneNumberWrapper.style.display = 'block'
          phoneNumberWrapper.setAttribute('aria-hidden', 'false')
          phoneNumberButton.setAttribute('aria-expanded', 'true')

          setTimeout(() => {
            phoneNumberWrapper.classList.add('card-team__phone-number--active')
          }, 100)
        } else {
          phoneNumberWrapper.classList.remove('card-team__phone-number--active')
          phoneNumberWrapper.setAttribute('aria-hidden', 'true')
          phoneNumberButton.setAttribute('aria-expanded', 'false')

          closingPhone = setTimeout(() => {
            phoneNumberWrapper.style.display = 'none'
          }, 100)
        }
      })
    }
  })
}

/* Single Post Related Posts Slider */
function postRelatedSlider () {
  const wrapper = document.querySelector('.single-post__related-content-wrapper.splide')

  // Initializing Spliders
  const progressBarWrapper = wrapper.querySelector('.splide__progress-bar-wrapper')
  const slider = new Splide(wrapper, {
    perPage: 1,
    arrows: true,
    gap: '1rem',
    drag: 'free',
    pagination: false,
    mediaQuery: 'min',
    padding: '1rem',
    breakpoints: {
      767: {
        destroy: true
      },
      481: {
        gap: '2rem',
        perPage: 2,
        padding: '2.5rem'
      }
    }
  })

  if (progressBarWrapper) {
    sliderProgressBarInIt(wrapper, slider, progressBarWrapper)
  }

  slider.mount()
}

// Initialize Drag Circle Cursor
function circleCursorInIt() {
  var body = document.querySelector('body');
  const cursorBall = document.getElementById('circleCursor');
  const cursorBallBg = document.getElementById('circleCursorBg');
  const cursorText = document.getElementById('cursorText');
  const draggers = document.querySelectorAll('.content-timeline__track');

  var hoverOutTimeout = '';
    
    function onMouseMove(e) {
    gsap.to(cursorBall, {duration: .4, x: e.pageX - 15 - window.scrollX, y: e.pageY - 15 - window.scrollY, ease:"expo.out"});
    }

    function onMouseHoverDrag(e) {
    if(hoverOutTimeout != '') {
      clearTimeout(hoverOutTimeout);
      hoverOutTimeout = '';
    }

    body.classList.add('hide-cursor');

    gsap.to(cursorBall, {duration: .3, scale: 1, opacity: 1, ease:"expo.out"});
    gsap.to(cursorBallBg, {duration: .3, scale: 2, ease:"expo.out"});
    cursorText.innerText = 'Drag';
    }

    function onMouseHoverOut(e) {
    if(hoverOutTimeout == '') {
      hoverOutTimeout = setTimeout(() => {
        body.classList.remove('hide-cursor');
        gsap.to(cursorBall, {duration: .2, scale: 0, opacity: 0, ease:"expo.out"});
        gsap.to(cursorBallBg, {duration: .2, scale: 0, ease:"expo.out"});
        cursorText.innerText = '';
      }, 100);
    }
    }
    
    document.body.addEventListener('mousemove', onMouseMove);

    for (let i = 0; i < draggers.length; i++) {
      draggers[i].addEventListener('mouseover', onMouseHoverDrag);
      draggers[i].addEventListener('mouseout', onMouseHoverOut);
    }
}

/* Initialize Everything */
document.addEventListener('DOMContentLoaded', function () {
  circleCursorInIt();

  if (document.getElementById('header')) {
    headerInIt()
  }

  /* Accessibility Cookie Fixes */
  if (getCookie('reduce-motion') === 'true') {
    const videos = document.querySelectorAll('video[autoplay]')
    videos.forEach((video) =>
      video.pause()
    )
  }

  if (getCookie('high-contrast') === 'true') {
    const html = document.querySelector('html')
    html.classList.add('high-contrast')
  }

  if (getCookie('reduce-motion') === 'true') {
    const html = document.querySelector('html')
    html.classList.add('reduce-motion')
  }

  /* Fade Ins */
  fadeIns()

  /* Accessibility Panel */
  if (document.getElementById('accessibilityPanelToggle')) {
    accessibilityInit()
  }

  /* Content - Card Slider */
  if (document.getElementsByClassName('content-card-slider').length > 0) {
    contentCardSliderInIt()
  }

  /* Content - Timeline */
  if (document.getElementsByClassName('content-timeline').length > 0) {
    contentTimelineInIt()
  }

  /* Content - Video Slider */
  if (document.getElementsByClassName('content-video-slider').length > 0) {
    contentVideoSliderInIt()
  }

  /* Content - Features Slider */
  if (document.getElementsByClassName('content-features').length > 0) {
    contentFeaturesInIt()
  }

  /* Related - Testimonials */
  if (document.getElementsByClassName('related-testimonials').length > 0) {
    relatedTestimonialsInIt()
  }

  /* Content - Logo Grid */
  if (document.getElementsByClassName('content-logo-grid__logos-grid splide').length > 0) {
    logoGridInIt()
  }

  /* Team Card */
  if (document.getElementsByClassName('card-team').length > 0) {
    teamCardInit()
  }

  /* Team Single */
  if (document.getElementsByClassName('content-team-single').length > 0) {
    teamSingleInit()
  }

  /* Single Post Related Slider */
  if (document.getElementsByClassName('single-post__related').length > 0) {
    postRelatedSlider()
  }
})
